
import {defineComponent, PropType} from "vue";
import {CodeDescriptionRestDto, ImagePositioningRestDto} from "@/api/pharma-cms-content-block/models";
import {ImagePositioningRestDtoModel} from "@/models/api/pharma-cms-content-block/ImagePositioningRestDtoModel";
import {ObjectFitEnum} from "@/api/enums/object-fit-enum";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import MetadataGroupCard from "@/components/layouts/content-pages/wysiwyg/misc/MetadataGroupCard.vue";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {PositionHorizontalEnum} from "@/api/enums/position-horizontal-enum";
import {PositionVerticalEnum} from "@/api/enums/position-vertical-enum";

export default defineComponent({
  name: "ImagePositioningDetail",
  mixins: [ComponentMixin],
  components: {GenericComponentWrapper, InputSelect, MetadataGroupCard},
  emits: ['update:modelValue'],
  props: {
    // Null means the group is switched off: the image uses SMA default positioning.
    modelValue: {
      type: Object as PropType<ImagePositioningRestDto | null>,
      required: false,
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.persistedDto = this.modelValue ? ImagePositioningRestDtoModel.cloneFrom(this.modelValue) : null;

    this.initialize();
  },
  data() {
    return {
      enabled: false as boolean,
      localDto: ImagePositioningRestDtoModel.createWithDefaults() as ImagePositioningRestDto,
      lastActiveDto: null as ImagePositioningRestDto | null,
      persistedDto: null as ImagePositioningRestDto | null,
      enumObjectFit: [] as CodeDescriptionRestDto[],
      enumPositionHorizontal: [] as CodeDescriptionRestDto[],
      enumPositionVertical: [] as CodeDescriptionRestDto[],
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: ImagePositioningRestDto | null) {
        this.enabled = !!newValue;
        if (newValue) {
          this.localDto = ImagePositioningRestDtoModel.cloneFrom(newValue);
        }
      },
    },
  },
  computed: {
    // Every size leaves the image free to sit somewhere within the container, except
    // NONE, which applies no object-fit at all.
    showPosition(): boolean {
      return !!this.localDto.object_fit_code
        && this.localDto.object_fit_code !== ObjectFitEnum.NONE;
    },
  },
  methods: {
    async doInitialize(): Promise<void> {
      this.enumObjectFit = await EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_FIT, DpModuleEnum.CMS);
      this.enumPositionHorizontal = [
        {code: PositionHorizontalEnum.LEFT, description: this.$t('left')},
        {code: PositionHorizontalEnum.CENTER, description: this.$t('center')},
        {code: PositionHorizontalEnum.RIGHT, description: this.$t('right')},
      ];
      this.enumPositionVertical = [
        {code: PositionVerticalEnum.TOP, description: this.$t('top')},
        {code: PositionVerticalEnum.CENTER, description: this.$t('center')},
        {code: PositionVerticalEnum.BOTTOM, description: this.$t('bottom')},
      ];
    },
    onEnabledChanged(newValue: boolean): void {
      // The switch re-emits its current state on render; acting on that would
      // bounce an update back up and re-derive `enabled` again, without end.
      if (newValue === this.enabled) {
        return;
      }
      this.enabled = newValue;

      if (newValue) {
        this.localDto = this.lastActiveDto
          ? ImagePositioningRestDtoModel.cloneFrom(this.lastActiveDto)
          : ImagePositioningRestDtoModel.createWithDefaults();

        this.$emit('update:modelValue', this.localDto);
      } else {
        this.lastActiveDto = ImagePositioningRestDtoModel.cloneFrom(this.localDto);

        this.$emit('update:modelValue', null);
      }
    },
    resetToPersisted(): void {
      this.localDto = this.persistedDto
        ? ImagePositioningRestDtoModel.cloneFrom(this.persistedDto)
        : ImagePositioningRestDtoModel.createWithDefaults();

      this.$emit('update:modelValue', this.localDto);
    },
    onObjectFitChanged(): void {
      if (this.showPosition) {
        if (!this.localDto.position_horizontal_code) {
          this.localDto.position_horizontal_code = PositionHorizontalEnum.CENTER;
        }
        if (!this.localDto.position_vertical_code) {
          this.localDto.position_vertical_code = PositionVerticalEnum.CENTER;
        }
      } else {
        this.localDto.position_horizontal_code = null;
        this.localDto.position_vertical_code = null;
      }

      this.emitModelChanged();
    },
    emitModelChanged(): void {
      this.$emit('update:modelValue', this.localDto);
    },
  },
});
